import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import PackAnalyticsView from './PackAnalyticsView';
import PackCapacityView from './PackCapacityView';
import { usePermissions } from '../../utils/usePermissions';
import './PacksManagement.css';

const API_URL = import.meta.env.VITE_API_URL;

// ─── État initial vide du formulaire ──────────────────────────────────────────
const emptyPackForm = {
    nom: '',
    reference: '',
    puissance: '',
    description: '',
    statut: 'actif',
    categories: [],       // [{ categorieId, produits: [{ produitId, quantiteRecommandee }] }]
    prixFinal: ''         // champ admin modifiable
};

// ─── Helpers prix ─────────────────────────────────────────────────────────────
const getPrixVente = (produit) => {
    if (!produit) return 0;
    if (produit.modeGestionStock === 'lineaire') {
        const base = produit.prixMetre || 0;
        return base + base * (produit.tauxGain || 0) / 100;
    }
    const base = produit.prixAchat || 0;
    return base + base * (produit.tauxGain || 0) / 100;
};

const getPrixBase = (produit) => {
    if (!produit) return 0;
    return produit.modeGestionStock === 'lineaire'
        ? (produit.prixMetre || 0)
        : (produit.prixAchat || 0);
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-TN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const normalizeText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const isSupportModulesTriangle = (catInfo, produit) =>
    normalizeText(catInfo?.label).includes('support module') &&
    normalizeText(produit?.nomProduit).includes('triangle');

const isSupportModulesCategory = (catInfo) =>
    normalizeText(catInfo?.label).includes('support module');

const findCatalogProduct = (produitsCatalog, produitId) =>
    produitsCatalog.find(p => String(p._id) === String(produitId));

const normalizeTriangleQuantity = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getTriangleBaseDecoupe = (item) => {
    const source = item.decoupeLineaireBase || item.decoupeLineaire || {};
    const sourcePieces = parseFloat(source.nombrePieces);
    const sourceMetres = parseFloat(source.metres);
    const longueurCandidate = parseFloat(source.longueurPieceMetre);
    const longueurPieceMetre = Math.max(
        0.001,
        longueurCandidate ||
            (sourceMetres && sourcePieces ? sourceMetres / sourcePieces : 0) ||
            parseFloat(item.quantiteRecommandee) ||
            1
    );

    return {
        nombrePieces: 1,
        longueurPieceMetre,
        metres: round3(longueurPieceMetre)
    };
};

const buildTriangleLinearItem = (item, quantiteTriangles, baseDecoupe = getTriangleBaseDecoupe(item)) => {
    const qTriangles = normalizeTriangleQuantity(quantiteTriangles);
    const longueurPieceMetre = Math.max(0.001, parseFloat(baseDecoupe.longueurPieceMetre) || 1);
    const nombrePieces = qTriangles;
    const metres = round3(nombrePieces * longueurPieceMetre);

    return {
        ...item,
        quantiteTriangles: qTriangles,
        decoupeLineaireBase: {
            nombrePieces: 1,
            longueurPieceMetre,
            metres: round3(longueurPieceMetre)
        },
        decoupeLineaire: {
            nombrePieces,
            longueurPieceMetre,
            metres
        },
        quantiteRecommandee: metres
    };
};

// ─── Composant principal ──────────────────────────────────────────────────────
const PacksManagement = ({ onNavigate, onLogout, currentPage }) => {
    const { hasPermission } = usePermissions();
    const packFormRef = useRef(null);

    // Onglets (packs, analytics, capacity)
    const [activeTab, setActiveTab] = useState('packs');

    // Données
    const [packs, setPacks] = useState([]);
    const [produitsCatalog, setProduitsCatalog] = useState([]);
    const [categories, setCategories] = useState([]);
    const [trianglesByCat, setTrianglesByCat] = useState({}); // { categorieId: [triangle] }
    const [triangleApplyQuantities, setTriangleApplyQuantities] = useState({});
    const [loading, setLoading] = useState(true);

    // UI Form
    const [showForm, setShowForm] = useState(false);
    const [editingPack, setEditingPack] = useState(null);
    const [duplicateSource, setDuplicateSource] = useState(null);
    const [packForm, setPackForm] = useState(emptyPackForm);

    // Filtres liste
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPuissance, setFilterPuissance] = useState('');
    const [filterStatut, setFilterStatut] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(10);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`
    });

    const fetchPacks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/packs`, {
                headers: getAuthHeaders(),
                params: { page, limit, search: searchTerm, puissance: filterPuissance, statut: filterStatut }
            });
            setPacks(res.data.packs || []);
            setTotalPages(res.data.pages || 1);
        } catch (err) {
            console.error('Erreur fetchPacks:', err);
            Swal.fire('Erreur', 'Impossible de charger les packs.', 'error');
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchTerm, filterPuissance, filterStatut]);

    const fetchCatalog = useCallback(async () => {
        try {
            const [prodRes, catRes] = await Promise.all([
                axios.get(`${API_URL}/produits`, { headers: getAuthHeaders() }),
                axios.get(`${API_URL}/categories`, { headers: getAuthHeaders() })
            ]);
            const prods = Array.isArray(prodRes.data) ? prodRes.data : (prodRes.data?.data || []);
            const cats = Array.isArray(catRes.data) ? catRes.data : (catRes.data?.data || []);
            setProduitsCatalog(prods);
            setCategories(cats);
        } catch (err) {
            console.error('Erreur fetchCatalog:', err);
        }
    }, []);

    useEffect(() => { fetchPacks(); }, [fetchPacks]);
    useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

    // Detecter editId / duplicateId depuis l'URL si provenance d'une autre page
    useEffect(() => {
        if (packs.length === 0) return;
        const urlParams = new URLSearchParams(window.location.search);
        const editId = urlParams.get('editId');
        const duplicateId = urlParams.get('duplicateId');

        if (editId) {
            const target = packs.find(p => String(p._id) === String(editId));
            if (target) handleOpenEdit(target);
        } else if (duplicateId) {
            const target = packs.find(p => String(p._id) === String(duplicateId));
            if (target) handleOpenDuplicate(target);
        }
    }, [packs]);

    // Scroll vers le formulaire à l'ouverture
    useEffect(() => {
        if (showForm) {
            const rafId = requestAnimationFrame(() => {
                packFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
            return () => cancelAnimationFrame(rafId);
        }
    }, [showForm]);

    // ── Calcul du prix estimé en live (HT, TVA, TTC) ─────────────────────────
    const liveFinancials = useMemo(() => {
        let totalHT = 0;
        let totalTVA = 0;
        for (const cat of packForm.categories) {
            for (const item of cat.produits) {
                if (item.produitId) {
                    const prod = findCatalogProduct(produitsCatalog, item.produitId);
                    if (prod) {
                        const prixHT = getPrixVente(prod);
                        const qte = item.quantiteRecommandee || 1;
                        const lineHT = prixHT * qte;
                        const tvaRate = prod.TVA !== undefined ? Number(prod.TVA) : 19;
                        const lineTVA = lineHT * (tvaRate / 100);
                        totalHT += lineHT;
                        totalTVA += lineTVA;
                    }
                } else if (item.nomProduit && item.prixUnitaire !== undefined) {
                    const lineHT = (Number(item.prixUnitaire) || 0) * (item.quantiteRecommandee || 1);
                    const lineTVA = lineHT * (19 / 100);
                    totalHT += lineHT;
                    totalTVA += lineTVA;
                }
            }
        }
        const totalTTC = round3(totalHT + totalTVA);
        totalHT = round3(totalHT);
        totalTVA = round3(totalTVA);
        return { totalHT, totalTVA, totalTTC };
    }, [packForm.categories, produitsCatalog]);

    const liveEstimatedPrice = liveFinancials.totalHT;

    // Mettre à jour prixFinal lorsque le prix calculé change (seulement si non modifié manuellement)
    const prixFinalLocked = useRef(false);
    useEffect(() => {
        if (!prixFinalLocked.current) {
            setPackForm(prev => ({ ...prev, prixFinal: liveFinancials.totalTTC }));
        }
    }, [liveFinancials.totalTTC]);

    // ── Handlers formulaire ────────────────────────────────────────────────────
    const handleViewDetail = (pack) => {
        if (!hasPermission('produits', 'read')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de consulter ce pack.", 'warning');
            return;
        }
        onNavigate('pack-detail', { id: pack._id });
    };

    const handleOpenCreate = () => {
        if (!hasPermission('produits', 'create')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de créer un pack.", 'warning');
            return;
        }
        setEditingPack(null);
        setDuplicateSource(null);
        setPackForm(emptyPackForm);
        prixFinalLocked.current = false;
        setShowForm(true);
    };

    const handleOpenEdit = (pack) => {
        if (!hasPermission('produits', 'update')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de modifier ce pack.", 'warning');
            return;
        }
        setEditingPack(pack);
        setDuplicateSource(null);

        // Reconstituer les catégories du formulaire depuis la réponse backend
        const formCategories = (pack.categories || []).map(cat => ({
            categorieId: cat.categorieId?._id || cat.categorieId,
            produits: (cat.produits || []).map(p => ({
                produitId: p.produitId?._id || p.produitId,
                nomProduit: p.nomProduit,
                prixUnitaire: p.prixUnitaire,
                quantiteRecommandee: p.quantiteRecommandee,
                decoupeLineaire: p.decoupeLineaire,
                decoupeLineaireBase: p.decoupeLineaireBase,
                quantiteTriangles: p.quantiteTriangles,
                triangleId: p.triangleId?._id || p.triangleId
            }))
        }));

        setPackForm({
            nom: pack.nom || '',
            reference: pack.reference || '',
            puissance: pack.puissance || '',
            description: pack.description || '',
            statut: pack.statut || 'actif',
            categories: formCategories,
            prixFinal: pack.prixFinal ?? pack.prixCalcule ?? ''
        });
        prixFinalLocked.current = true;
        setShowForm(true);
    };

    const handleOpenDuplicate = (pack) => {
        if (!hasPermission('produits', 'create')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de dupliquer ce pack.", 'warning');
            return;
        }

        setEditingPack(null);
        setDuplicateSource(pack);

        // Préremplir le formulaire avec les données du pack source
        const formCategories = (pack.categories || []).map(cat => ({
            categorieId: cat.categorieId?._id || cat.categorieId,
            produits: (cat.produits || []).map(p => ({
                produitId: p.produitId?._id || p.produitId,
                nomProduit: p.nomProduit,
                prixUnitaire: p.prixUnitaire,
                quantiteRecommandee: p.quantiteRecommandee,
                decoupeLineaire: p.decoupeLineaire,
                decoupeLineaireBase: p.decoupeLineaireBase,
                quantiteTriangles: p.quantiteTriangles,
                triangleId: p.triangleId?._id || p.triangleId
            }))
        }));

        const suggestedRef = pack.reference ? `${pack.reference}-COPY` : `PK-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;

        setPackForm({
            nom: `${pack.nom} (Copie)`,
            reference: suggestedRef,
            puissance: pack.puissance || '',
            description: pack.description || '',
            statut: pack.statut || 'actif',
            categories: formCategories,
            prixFinal: pack.prixFinal ?? pack.prixCalcule ?? ''
        });
        prixFinalLocked.current = true;
        setShowForm(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!packForm.nom || !packForm.puissance) {
            Swal.fire('Champs manquants', 'Le nom et la puissance sont requis.', 'warning');
            return;
        }
        const totalProduits = packForm.categories.reduce((s, c) => s + c.produits.length, 0);
        if (totalProduits === 0) {
            Swal.fire('Aucun produit', 'Veuillez ajouter au moins un produit dans le pack.', 'warning');
            return;
        }

        const payload = {
            nom: packForm.nom,
            reference: packForm.reference ? packForm.reference.trim() : undefined,
            puissance: packForm.puissance,
            description: packForm.description,
            statut: packForm.statut,
            categories: packForm.categories,
            prixFinal: Number(packForm.prixFinal) || liveEstimatedPrice
        };

        try {
            if (editingPack) {
                await axios.put(`${API_URL}/packs/${editingPack._id}`, payload, { headers: getAuthHeaders() });
                Swal.fire('Modifié !', 'Le pack a été modifié avec succès.', 'success');
                setShowForm(false);
                setEditingPack(null);
                setDuplicateSource(null);
                setPackForm(emptyPackForm);
                prixFinalLocked.current = false;
                fetchPacks();
            } else {
                const res = await axios.post(`${API_URL}/packs`, payload, { headers: getAuthHeaders() });
                const newPack = res.data?.pack || res.data;

                if (duplicateSource) {
                    await Swal.fire({
                        title: 'Pack dupliqué !',
                        text: `Le pack a été créé avec succès à partir du pack [${duplicateSource.nom}].`,
                        icon: 'success',
                        confirmButtonText: 'Voir les détails du nouveau pack'
                    });
                } else {
                    await Swal.fire('Créé !', 'Le pack a été créé avec succès.', 'success');
                }

                setShowForm(false);
                setEditingPack(null);
                setDuplicateSource(null);
                setPackForm(emptyPackForm);
                prixFinalLocked.current = false;

                if (newPack && newPack._id) {
                    onNavigate('pack-detail', { id: newPack._id });
                } else {
                    fetchPacks();
                }
            }
        } catch (err) {
            console.error('Erreur enregistrement pack:', err);
            Swal.fire('Erreur', err.response?.data?.message || "Une erreur s'est produite lors de l'enregistrement.", 'error');
        }
    };
          

    const handleDeletePack = async (packId) => {
        if (!hasPermission('produits', 'delete')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de supprimer ce pack.", 'warning');
            return;
        }
        const result = await Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: 'Cette action est irréversible !',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
        });
        if (result.isConfirmed) {
            try {
                await axios.delete(`${API_URL}/packs/${packId}`, { headers: getAuthHeaders() });
                Swal.fire('Supprimé !', 'Le pack a été supprimé avec succès.', 'success');
                fetchPacks();
            } catch (err) {
                Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la suppression.', 'error');
            }
        }
    };

    // ── Gestion des catégories dans le formulaire ──────────────────────────────
    const addCategory = () => {
        // Catégories disponibles (non encore ajoutées au pack)
        const usedIds = packForm.categories.map(c => c.categorieId);
        const available = categories.filter(c => !usedIds.includes(c._id));
        if (available.length === 0) {
            Swal.fire('Info', 'Toutes les catégories disponibles ont déjà été ajoutées.', 'info');
            return;
        }
        setPackForm(prev => ({
            ...prev,
            categories: [...prev.categories, { categorieId: available[0]._id, produits: [] }]
        }));
    };

    const updateCategoryId = (catIndex, newCatId) => {
        // Vérifier que la catégorie n'est pas déjà utilisée
        const alreadyUsed = packForm.categories.some((c, i) => i !== catIndex && c.categorieId === newCatId);
        if (alreadyUsed) {
            Swal.fire('Doublon', 'Cette catégorie est déjà présente dans le pack.', 'warning');
            return;
        }
        const updated = [...packForm.categories];
        updated[catIndex] = { ...updated[catIndex], categorieId: newCatId, produits: [] };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    const removeCategory = (catIndex) => {
        const cat = packForm.categories[catIndex];
        if (cat) {
            // Nettoyer les triangles mis en cache pour cette catégorie
            setTrianglesByCat(prev => {
                const next = { ...prev };
                delete next[cat.categorieId];
                return next;
            });
        }
        const updated = packForm.categories.filter((_, i) => i !== catIndex);
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    // ── Chargement des triangles d'une catégorie ──────────────────────────────
    const loadTrianglesForCat = async (categorieId) => {
        if (!categorieId || trianglesByCat[categorieId] !== undefined) return;
        try {
            const res = await axios.get(`${API_URL}/triangles/by-categorie/${categorieId}`, {
                headers: getAuthHeaders()
            });
            setTrianglesByCat(prev => ({ ...prev, [categorieId]: res.data || [] }));
        } catch (err) {
            console.error('Erreur loadTrianglesForCat:', err);
            setTrianglesByCat(prev => ({ ...prev, [categorieId]: [] }));
        }
    };

    // ── Appliquer un triangle à une catégorie du pack ─────────────────────────
    const getTriangleApplyKey = (categorieId, triangleId) => `${categorieId || 'cat'}:${triangleId || 'triangle'}`;

    const getTriangleApplyQuantity = (categorieId, triangleId) =>
        normalizeTriangleQuantity(triangleApplyQuantities[getTriangleApplyKey(categorieId, triangleId)]);

    const updateTriangleApplyQuantity = (categorieId, triangleId, value) => {
        const key = getTriangleApplyKey(categorieId, triangleId);
        setTriangleApplyQuantities(prev => ({
            ...prev,
            [key]: normalizeTriangleQuantity(value)
        }));
    };

    const applyTriangleToPack = (catIndex, triangle, quantiteTriangles = 1) => {
        const catInfo = categories.find(c => c._id === packForm.categories[catIndex]?.categorieId);
        const selectedTriangleQuantity = normalizeTriangleQuantity(quantiteTriangles);
        const allUsedProdIds = new Set(
            packForm.categories.flatMap(c => c.produits.map(p => p.produitId))
        );
        let added = 0;
        let skipped = 0;
        const newProduits = [...packForm.categories[catIndex].produits];

        for (const item of (triangle.produits || [])) {
            const prodId = item.produitId?._id || item.produitId;
            const prodDef = item.produitId?._id ? item.produitId : findCatalogProduct(produitsCatalog, prodId);
            const isLineaire = item.produitId?.modeGestionStock === 'lineaire' || 
                               prodDef?.modeGestionStock === 'lineaire';
            
            if (!isLineaire && allUsedProdIds.has(prodId)) {
                skipped++;
                continue;
            }
            
            let decoupe = item.decoupeLineaire;
            if (isLineaire && !decoupe) {
                const qte = item.quantiteRecommandee || 1;
                decoupe = { nombrePieces: 1, longueurPieceMetre: qte, metres: qte };
            }

            const newItem = { 
                produitId: prodId, 
                quantiteRecommandee: item.quantiteRecommandee || 1,
                decoupeLineaire: decoupe,
                triangleId: triangle._id
            };
            const shouldApplyTriangleQuantity = isLineaire && (
                isSupportModulesTriangle(catInfo, prodDef) ||
                (isSupportModulesCategory(catInfo) && triangle._id)
            );

            newProduits.push(
                shouldApplyTriangleQuantity
                    ? buildTriangleLinearItem(newItem, selectedTriangleQuantity, decoupe)
                    : newItem
            );
            allUsedProdIds.add(prodId);
            added++;
        }

        const updated = [...packForm.categories];
        updated[catIndex] = { ...updated[catIndex], produits: newProduits };
        setPackForm(prev => ({ ...prev, categories: updated }));

        Swal.fire({
            title: `△ Triangle appliqué`,
            html: `<b>${added}</b> produit(s) ajouté(s)${skipped > 0 ? ` &bull; <b>${skipped}</b> déjà présent(s) ignoré(s)` : ''}.`,
            icon: 'success',
            timer: 2500,
            showConfirmButton: false
        });
    };

    // ── Gestion des produits dans une catégorie ────────────────────────────────
    const addProductToCategory = (catIndex, produitId) => {
        if (!produitId) return;

        const prodDef = findCatalogProduct(produitsCatalog, produitId);
        const catInfo = categories.find(c => c._id === packForm.categories[catIndex]?.categorieId);
        const isLineaire = prodDef && prodDef.modeGestionStock === 'lineaire';

        if (!isLineaire) {
            // Vérifier qu'il n'est pas déjà dans le pack (toutes catégories)
            for (const cat of packForm.categories) {
                if (cat.produits.some(p => p.produitId === produitId)) {
                    Swal.fire('Doublon', 'Ce produit est déjà présent dans le pack.', 'warning');
                    return;
                }
            }
        }

        const updated = [...packForm.categories];
        const newItem = isLineaire && isSupportModulesTriangle(catInfo, prodDef)
            ? buildTriangleLinearItem({
                produitId,
                quantiteRecommandee: 1,
                decoupeLineaire: { nombrePieces: 1, longueurPieceMetre: 1, metres: 1 }
            }, 1)
            : { produitId, quantiteRecommandee: 1 };

        updated[catIndex] = {
            ...updated[catIndex],
            produits: [...updated[catIndex].produits, newItem]
        };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    const removeProductFromCategory = (catIndex, prodIndex) => {
        const updated = [...packForm.categories];
        updated[catIndex] = {
            ...updated[catIndex],
            produits: updated[catIndex].produits.filter((_, i) => i !== prodIndex)
        };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    const addLigneLibreToCategory = (catIndex) => {
        const updated = [...packForm.categories];
        updated[catIndex] = {
            ...updated[catIndex],
            produits: [...updated[catIndex].produits, { 
                produitId: null, 
                nomProduit: 'Ligne libre', 
                prixUnitaire: 0, 
                quantiteRecommandee: 1 
            }]
        };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    const updateLigneLibre = (catIndex, prodIndex, field, value) => {
        const updated = [...packForm.categories];
        const prods = [...updated[catIndex].produits];
        prods[prodIndex] = { ...prods[prodIndex], [field]: value };
        updated[catIndex] = { ...updated[catIndex], produits: prods };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    const updateProductQty = (catIndex, prodIndex, qty) => {
        const updated = [...packForm.categories];
        const prods = [...updated[catIndex].produits];
        prods[prodIndex] = { ...prods[prodIndex], quantiteRecommandee: qty };
        updated[catIndex] = { ...updated[catIndex], produits: prods };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    const updateProductLinear = (catIndex, prodIndex, field, value) => {
        const updated = [...packForm.categories];
        const prods = [...updated[catIndex].produits];
        const prod = { ...prods[prodIndex] };
        const catInfo = categories.find(c => c._id === updated[catIndex]?.categorieId);
        const prodDef = findCatalogProduct(produitsCatalog, prod.produitId);
        const isTriangleSupportModule = isSupportModulesTriangle(catInfo, prodDef);

        if (!prod.decoupeLineaire) {
            // Initialisation avec la quantité recommandee existante si possible
            const existantMetres = parseFloat(prod.quantiteRecommandee) || 1;
            prod.decoupeLineaire = { nombrePieces: 1, longueurPieceMetre: existantMetres, metres: existantMetres };
        } else {
            prod.decoupeLineaire = { ...prod.decoupeLineaire };
        }

        if (isTriangleSupportModule) {
            let qTriangles = normalizeTriangleQuantity(prod.quantiteTriangles);
            const baseDecoupe = getTriangleBaseDecoupe(prod);

            if (field === 'nombrePieces') {
                qTriangles = normalizeTriangleQuantity(value);
            } else if (field === 'longueurPieceMetre') {
                baseDecoupe.longueurPieceMetre = Math.max(0.001, parseFloat(value) || 1);
            }

            prods[prodIndex] = buildTriangleLinearItem(prod, qTriangles, baseDecoupe);
            updated[catIndex] = { ...updated[catIndex], produits: prods };
            setPackForm(prev => ({ ...prev, categories: updated }));
            return;
        }

        prod.decoupeLineaire[field] = value;
        
        const np = parseFloat(prod.decoupeLineaire.nombrePieces) || 0;
        const lp = parseFloat(prod.decoupeLineaire.longueurPieceMetre) || 0;
        prod.decoupeLineaire.metres = np * lp;
        prod.quantiteRecommandee = prod.decoupeLineaire.metres;

        prods[prodIndex] = prod;
        updated[catIndex] = { ...updated[catIndex], produits: prods };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    // ── Quantite de triangles ──────────────────────────────────────────────────
    const updateTriangleQuantity = (catIndex, prodIndex, value) => {
        const updated = [...packForm.categories];
        const prods = [...updated[catIndex].produits];
        const prod = { ...prods[prodIndex] };

        prods[prodIndex] = buildTriangleLinearItem(
            prod,
            normalizeTriangleQuantity(value),
            getTriangleBaseDecoupe(prod)
        );
        updated[catIndex] = { ...updated[catIndex], produits: prods };
        setPackForm(prev => ({ ...prev, categories: updated }));
    };

    // ── Table récapitulative ───────────────────────────────────────────────────
    const recapRows = useMemo(() => {
        const rows = [];
        for (const cat of packForm.categories) {
            const catInfo = categories.find(c => c._id === cat.categorieId);
            for (const item of cat.produits) {
                if (item.produitId) {
                    const prod = findCatalogProduct(produitsCatalog, item.produitId);
                    if (!prod) continue;
                    const prixBase = getPrixBase(prod);
                    const prixVente = getPrixVente(prod);
                    const sousTotal = prixVente * (item.quantiteRecommandee || 1);
                    rows.push({
                        categorieLabel: catInfo?.label || '—',
                        nomProduit: prod.nomProduit,
                        modeGestionStock: prod.modeGestionStock,
                        quantite: item.quantiteRecommandee,
                        decoupeLineaire: item.decoupeLineaire,
                        decoupeLineaireBase: item.decoupeLineaireBase,
                        quantiteTriangles: item.quantiteTriangles,
                        prixBase,
                        tauxGain: prod.tauxGain || 0,
                        prixVente,
                        sousTotal
                    });
                } else {
                    const sousTotal = Number(item.prixUnitaire) * (item.quantiteRecommandee || 1);
                    rows.push({
                        categorieLabel: catInfo?.label || '—',
                        nomProduit: item.nomProduit || 'Ligne libre',
                        modeGestionStock: 'unite',
                        quantite: item.quantiteRecommandee,
                        decoupeLineaire: null,
                        prixBase: Number(item.prixUnitaire) || 0,
                        tauxGain: 0,
                        prixVente: Number(item.prixUnitaire) || 0,
                        sousTotal
                    });
                }
            }
        }
        return rows;
    }, [packForm.categories, produitsCatalog, categories]);

    // ── Filtres liste ──────────────────────────────────────────────────────────
    const handleClearFilters = () => {
        setSearchTerm('');
        setFilterPuissance('');
        setFilterStatut('');
        setPage(1);
    };

    // ── Rendu ──────────────────────────────────────────────────────────────────
    return (
        <div className="pm-layout">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <div className="pm-content">
                <div className="pm-main">

                    {/* ── Header ───────────────────────────────────────────── */}
                    <div className="pm-header packs-header-accent">
                        <div className="pm-header-text">
                            <h1>Gestion des Packs Produits</h1>
                            <p>Créez et configurez des packs de produits solaires organisés par catégorie.</p>
                        </div>
                        {hasPermission('produits', 'create') && (
                            <button className="pm-btn-add-hero" onClick={handleOpenCreate}>
                                ➕ Nouveau Pack
                            </button>
                        )}
                    </div>

                    {/* ── Tabs Navigation ───────────────────────────────────────────── */}
                    <div className="pm-tabs-navigation">
                        <button
                            className={`pm-tab-btn ${activeTab === 'packs' ? 'active' : ''}`}
                            onClick={() => setActiveTab('packs')}
                        >
                            📦 Gestion des Packs
                        </button>
                        <button
                            className={`pm-tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
                            onClick={() => setActiveTab('analytics')}
                        >
                            📊 Analyse & Rentabilité
                        </button>
                        <button
                            className={`pm-tab-btn ${activeTab === 'capacity' ? 'active' : ''}`}
                            onClick={() => setActiveTab('capacity')}
                        >
                            ⚡ Capacité Stock & Prévisions
                        </button>
                    </div>

                    {activeTab === 'analytics' && <PackAnalyticsView />}
                    {activeTab === 'capacity' && <PackCapacityView />}

                    {activeTab === 'packs' && (
                        <>
                            {/* ── Formulaire création/modification ────────────────── */}
                    {showForm && (
                        <div className="pm-form-card pack-form-card" ref={packFormRef}>
                            {duplicateSource && (
                                <div className="pack-duplication-banner">
                                    <span className="dup-banner-icon">ℹ️</span>
                                    <div className="dup-banner-text">
                                        <strong>Nouveau pack basé sur :</strong> <span>[{duplicateSource.nom}]</span>
                                    </div>
                                </div>
                            )}

                            <h3>
                                {editingPack
                                    ? `Modifier : ${editingPack.nom}`
                                    : duplicateSource
                                        ? `Dupliquer : ${duplicateSource.nom}`
                                        : 'Créer un Nouveau Pack'}
                            </h3>

                            <form onSubmit={handleFormSubmit}>
                                {/* ─ Informations générales ─ */}
                                <div className="pack-section-title">📋 Informations générales</div>
                                <div className="pm-form-row">
                                    <div className="pm-form-group">
                                        <label htmlFor="pack-nom">Nom du Pack *</label>
                                        <input
                                            id="pack-nom"
                                            type="text"
                                            value={packForm.nom}
                                            onChange={e => setPackForm({ ...packForm, nom: e.target.value })}
                                            placeholder="Ex: Pack Solaire Premium 5kWc"
                                            required
                                        />
                                    </div>
                                    <div className="pm-form-group">
                                        <label htmlFor="pack-reference">Référence du Pack (Optionnelle)</label>
                                        <input
                                            id="pack-reference"
                                            type="text"
                                            value={packForm.reference}
                                            onChange={e => setPackForm({ ...packForm, reference: e.target.value })}
                                            placeholder="Ex: PK-2026-001 (générée automatiquement si vide)"
                                        />
                                    </div>
                                    <div className="pm-form-group">
                                        <label htmlFor="pack-puissance">Puissance (kWc) *</label>
                                        <input
                                            id="pack-puissance"
                                            type="text"
                                            value={packForm.puissance}
                                            onChange={e => setPackForm({ ...packForm, puissance: e.target.value })}
                                            placeholder="Ex: 5 kWc"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="pm-form-row">
                                    <div className="pm-form-group">
                                        <label htmlFor="pack-description">Description</label>
                                        <textarea
                                            id="pack-description"
                                            value={packForm.description}
                                            onChange={e => setPackForm({ ...packForm, description: e.target.value })}
                                            placeholder="Détails sur la composition, l'onduleur, les panneaux..."
                                            rows="3"
                                        />
                                    </div>
                                    <div className="pm-form-group">
                                        <label htmlFor="pack-statut">Statut</label>
                                        <select
                                            id="pack-statut"
                                            value={packForm.statut}
                                            onChange={e => setPackForm({ ...packForm, statut: e.target.value })}
                                        >
                                            <option value="actif">Actif</option>
                                            <option value="inactif">Inactif</option>
                                        </select>
                                    </div>
                                </div>

                                {/* ─ Composition du pack ─ */}
                                <div className="pack-section-title">
                                    🗂️ Composition du Pack
                                    <button
                                        type="button"
                                        className="pack-add-cat-btn"
                                        onClick={addCategory}
                                        disabled={categories.length === 0}
                                    >
                                        + Ajouter une catégorie
                                    </button>
                                </div>

                                {packForm.categories.length === 0 && (
                                    <div className="pack-empty-hint">
                                        Cliquez sur « + Ajouter une catégorie » pour commencer à composer votre pack.
                                    </div>
                                )}

                                {packForm.categories.map((cat, catIndex) => {
                                    const catInfo = categories.find(c => c._id === cat.categorieId);
                                    const usedProductIds = new Set(
                                        packForm.categories.flatMap(c => c.produits.map(p => p.produitId))
                                    );
                                    // Produits disponibles : appartenant à cette catégorie ET pas encore dans le pack
                                    const availableProducts = produitsCatalog.filter(p => {
                                        const pCatId = p.categorie?._id || p.categorie;
                                        return pCatId === cat.categorieId && !usedProductIds.has(p._id);
                                    });
                                    // Catégories disponibles pour ce slot
                                    const usedCatIds = new Set(packForm.categories.map(c => c.categorieId));
                                    const availableCats = categories.filter(c =>
                                        c._id === cat.categorieId || !usedCatIds.has(c._id)
                                    );

                                    return (
                                        <div key={catIndex} className="pack-category-block">
                                            <div className="pack-category-header">
                                                <span className="pack-cat-icon">📁</span>
                                                <select
                                                    className="pack-cat-select"
                                                    value={cat.categorieId}
                                                    onChange={e => {
                                                        updateCategoryId(catIndex, e.target.value);
                                                        loadTrianglesForCat(e.target.value);
                                                    }}
                                                >
                                                    {availableCats.map(c => (
                                                        <option key={c._id} value={c._id}>{c.label}</option>
                                                    ))}
                                                </select>
                                                <span className="pack-cat-count">
                                                    {cat.produits.length} produit{cat.produits.length !== 1 ? 's' : ''}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="pack-remove-cat-btn"
                                                    onClick={() => removeCategory(catIndex)}
                                                    title="Supprimer cette catégorie"
                                                >
                                                    ✕
                                                </button>
                                            </div>

                                            {/* Produits de cette catégorie */}
                                            <div className="pack-products-in-cat">
                                                {cat.produits.map((item, prodIndex) => {
                                                    let isLibre = !item.produitId;
                                                    let prod = null;
                                                    let prixBase = 0, prixVente = 0, sousTotal = 0;

                                                    if (!isLibre) {
                                                        prod = findCatalogProduct(produitsCatalog, item.produitId);
                                                        prixBase = getPrixBase(prod);
                                                        prixVente = getPrixVente(prod);
                                                        sousTotal = prixVente * (item.quantiteRecommandee || 1);
                                                    } else {
                                                        prixBase = Number(item.prixUnitaire) || 0;
                                                        prixVente = Number(item.prixUnitaire) || 0;
                                                        sousTotal = prixVente * (item.quantiteRecommandee || 1);
                                                    }
                                                    const isTriangleSupportModule = !isLibre && isSupportModulesTriangle(catInfo, prod);

                                                    return (
                                                        <div key={prodIndex} className="pack-product-row">
                                                            <div className="pack-prod-info">
                                                                {isLibre ? (
                                                                    <input
                                                                        type="text"
                                                                        value={item.nomProduit}
                                                                        onChange={e => updateLigneLibre(catIndex, prodIndex, 'nomProduit', e.target.value)}
                                                                        placeholder="Nom du produit..."
                                                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '0.9em', width: '200px' }}
                                                                    />
                                                                ) : (
                                                                    <span className="pack-prod-name">
                                                                        {prod?.nomProduit || 'Produit inconnu'}
                                                                    </span>
                                                                )}
                                                                {prod?.modeGestionStock === 'lineaire' && (
                                                                    <span className="pack-mode-badge lineaire">Linéaire</span>
                                                                )}
                                                            </div>
                                                            <div className="pack-prod-prices">
                                                                {isLibre ? (
                                                                    <div className="pack-prod-qty">
                                                                        <label>Prix Unitaire</label>
                                                                        <input
                                                                            type="number" min="0" step="any"
                                                                            value={item.prixUnitaire}
                                                                            onChange={e => updateLigneLibre(catIndex, prodIndex, 'prixUnitaire', parseFloat(e.target.value) || 0)}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <span className="pack-price-cell">
                                                                            <small>Achat</small>
                                                                            {fmt(prixBase)} DT
                                                                        </span>
                                                                        <span className="pack-price-cell">
                                                                            <small>Gain</small>
                                                                            {prod?.tauxGain || 0}%
                                                                        </span>
                                                                        <span className="pack-price-cell highlight">
                                                                            <small>Vente</small>
                                                                            {fmt(prixVente)} DT
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {prod?.modeGestionStock === 'lineaire' ? (
                                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                                    {isTriangleSupportModule && (
                                                                        <div className="pack-prod-qty pack-triangle-qty">
                                                                            <label>Quantité de triangles</label>
                                                                            <input
                                                                                type="number"
                                                                                min="1"
                                                                                step="1"
                                                                                value={normalizeTriangleQuantity(item.quantiteTriangles)}
                                                                                onChange={e => updateTriangleQuantity(catIndex, prodIndex, e.target.value)}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <div className="pack-prod-qty">
                                                                        <label>Pièces</label>
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            value={item.decoupeLineaire?.nombrePieces !== undefined ? item.decoupeLineaire.nombrePieces : 1}
                                                                            onChange={e => updateProductLinear(catIndex, prodIndex, 'nombrePieces', e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <div className="pack-prod-qty">
                                                                        <label>Long. (m)</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0.001" step="any"
                                                                            value={item.decoupeLineaire?.longueurPieceMetre !== undefined ? item.decoupeLineaire.longueurPieceMetre : (item.quantiteRecommandee || 1)}
                                                                            onChange={e => updateProductLinear(catIndex, prodIndex, 'longueurPieceMetre', e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <div className="pack-prod-qty">
                                                                        <label>Total (m)</label>
                                                                        <input type="number" readOnly value={item.quantiteRecommandee} style={{background: '#f1f5f9', cursor: 'not-allowed'}} />
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="pack-prod-qty">
                                                                    <label>Qté (u)</label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        value={item.quantiteRecommandee}
                                                                        onChange={e => updateProductQty(catIndex, prodIndex, e.target.value)}
                                                                    />
                                                                </div>
                                                            )}
                                                            <span className="pack-prod-subtotal">
                                                                <small>Sous-total</small>
                                                                {fmt(sousTotal)} DT
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="pack-remove-prod-btn"
                                                                onClick={() => removeProductFromCategory(catIndex, prodIndex)}
                                                                title="Retirer ce produit"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    );
                                                })}

                                                {/* Triangles disponibles pour cette catégorie */}
                                                {(() => {
                                                    const tris = trianglesByCat[cat.categorieId];
                                                    if (tris === undefined) {
                                                        // Déclencher le chargement si pas encore fait
                                                        loadTrianglesForCat(cat.categorieId);
                                                        return null;
                                                    }
                                                    if (!tris || tris.length === 0) return null;
                                                    const isSupportModulesCat = normalizeText(catInfo?.label) === 'support modules';
                                                    return (
                                                        <div className="pack-triangles-panel">
                                                            <div className="pack-triangles-label">△ Triangles disponibles</div>
                                                            <div className="pack-triangles-list">
                                                                {tris.map(tri => {
                                                                    const triangleQuantity = getTriangleApplyQuantity(cat.categorieId, tri._id);
                                                                    return (
                                                                    <div key={tri._id} className="pack-triangle-chip">
                                                                        {tri.image && (
                                                                            <img
                                                                                src={`${API_URL.replace('/api', '')}${tri.image}`}
                                                                                alt={tri.nom}
                                                                                className="pack-tri-chip-img"
                                                                            />
                                                                        )}
                                                                        <span className="pack-tri-chip-name">{tri.nom}</span>
                                                                        <span className="pack-tri-chip-count">{tri.produits?.length || 0} produits</span>
                                                                        {isSupportModulesCat && (
                                                                            <label className="pack-tri-qty-field">
                                                                                <span>Qté triangle</span>
                                                                                <input
                                                                                    type="number"
                                                                                    min="1"
                                                                                    step="1"
                                                                                    value={triangleQuantity}
                                                                                    onChange={e => updateTriangleApplyQuantity(cat.categorieId, tri._id, e.target.value)}
                                                                                />
                                                                            </label>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            className="pack-tri-apply-btn"
                                                                            onClick={() => applyTriangleToPack(catIndex, tri, triangleQuantity)}
                                                                            title="Appliquer ce triangle"
                                                                        >
                                                                            Appliquer
                                                                        </button>
                                                                    </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* Sélecteur d'ajout de produit */}
                                                <div className="pack-add-product-row" style={{ display: 'flex', gap: '10px' }}>
                                                    <select
                                                        className="pack-add-prod-select"
                                                        defaultValue=""
                                                        onChange={e => {
                                                            if (e.target.value) {
                                                                addProductToCategory(catIndex, e.target.value);
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                        style={{ flex: 1 }}
                                                    >
                                                        <option value="" disabled>
                                                            + Ajouter un produit ({catInfo?.label || '...'})
                                                        </option>
                                                        {availableProducts.map(p => (
                                                            <option key={p._id} value={p._id}>
                                                                {p.nomProduit}
                                                                {p.modeGestionStock === 'lineaire' ? ' [m]' : ''}
                                                            </option>
                                                        ))}
                                                        {availableProducts.length === 0 && (
                                                            <option disabled>Aucun produit disponible dans cette catégorie</option>
                                                        )}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        onClick={() => addLigneLibreToCategory(catIndex)}
                                                        style={{
                                                            background: 'rgba(255,255,255,0.1)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            padding: '0 15px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.9em'
                                                        }}
                                                    >
                                                        + Saisie Libre
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* ─ Tableau récapitulatif ─ */}
                                {recapRows.length > 0 && (
                                    <div className="pack-recap-section">
                                        <div className="pack-section-title">📊 Tableau Récapitulatif</div>
                                        <div className="pack-recap-table-wrap">
                                            <table className="pack-recap-table">
                                                <thead>
                                                    <tr>
                                                        <th>Catégorie</th>
                                                        <th>Produit</th>
                                                        <th>Mode</th>
                                                        <th>Qté</th>
                                                        <th>Prix Achat</th>
                                                        <th>Gain %</th>
                                                        <th>Prix Vente</th>
                                                        <th>Sous-total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {recapRows.map((row, i) => (
                                                        <tr key={i}>
                                                            <td>
                                                                <span className="recap-cat-label">{row.categorieLabel}</span>
                                                            </td>
                                                            <td className="recap-prod-name">{row.nomProduit}</td>
                                                            <td>
                                                                <span className={`pack-mode-badge ${row.modeGestionStock}`}>
                                                                    {row.modeGestionStock === 'lineaire' ? 'Linéaire' : 'Unité'}
                                                                </span>
                                                            </td>
                                                            <td className="recap-num">
                                                                {row.modeGestionStock === 'lineaire' ? (
                                                                    row.decoupeLineaire && row.decoupeLineaire.longueurPieceMetre ? (
                                                                        <span title={`${row.decoupeLineaire.nombrePieces || 1} pièce(s) × ${row.decoupeLineaire.longueurPieceMetre} m`}>
                                                                            {row.quantiteTriangles
                                                                                ? `${row.quantiteTriangles} triangles - ${row.decoupeLineaire.nombrePieces}x${Number(row.decoupeLineaire.longueurPieceMetre).toFixed(2)}m = ${Number(row.quantite).toFixed(2)}m`
                                                                                : (row.decoupeLineaire.nombrePieces || 1) > 1
                                                                                    ? `${row.decoupeLineaire.nombrePieces}×${Number(row.decoupeLineaire.longueurPieceMetre).toFixed(2)}m = ${Number(row.quantite).toFixed(2)}m`
                                                                                    : `${Number(row.decoupeLineaire.longueurPieceMetre).toFixed(2)} m`
                                                                            }
                                                                        </span>
                                                                    ) : `${row.quantite} m`
                                                                ) : row.quantite}
                                                            </td>
                                                            <td className="recap-num">{fmt(row.prixBase)} DT</td>
                                                            <td className="recap-num">{row.tauxGain}%</td>
                                                            <td className="recap-num recap-prix-vente">{fmt(row.prixVente)} DT</td>
                                                            <td className="recap-num recap-subtotal">{fmt(row.sousTotal)} DT</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <td colSpan="7" className="recap-total-label">Total HT Calculé</td>
                                                        <td className="recap-num recap-total">{fmt(liveFinancials.totalHT)} DT</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ─ Résumé financier ─ */}
                                <div className="pack-financial-summary">
                                    <div className="pack-section-title">💰 Résumé Financier</div>
                                    <div className="pack-finance-grid">
                                        <div className="pack-finance-card auto">
                                            <span className="finance-label">Total montant sans TVA</span>
                                            <span className="finance-value">{fmt(liveFinancials.totalHT)} DT</span>
                                            <span className="finance-hint">Somme des prix unitaires HT × quantités</span>
                                        </div>
                                        <div className="pack-finance-card auto">
                                            <span className="finance-label">Total montant TVA</span>
                                            <span className="finance-value">{fmt(liveFinancials.totalTVA)} DT</span>
                                            <span className="finance-hint">Montant total TVA des produits (ex. 19%)</span>
                                        </div>
                                        <div className="pack-finance-card auto">
                                            <span className="finance-label">Montant final TTC</span>
                                            <span className="finance-value">{fmt(liveFinancials.totalTTC)} DT</span>
                                            <span className="finance-hint">Total HT + Total TVA calculé</span>
                                        </div>
                                        <div className="pack-finance-card admin">
                                            <label className="finance-label" htmlFor="pack-prix-final">Prix Final Administrateur</label>
                                            <input
                                                id="pack-prix-final"
                                                type="number"
                                                min="0"
                                                step="0.001"
                                                className="finance-input"
                                                value={packForm.prixFinal}
                                                onChange={e => {
                                                    prixFinalLocked.current = true;
                                                    setPackForm({ ...packForm, prixFinal: e.target.value });
                                                }}
                                                placeholder={fmt(liveFinancials.totalTTC)}
                                            />
                                            <span className="finance-hint">Modifiable manuellement — conservé en base</span>
                                        </div>
                                    </div>
                                    {prixFinalLocked.current && (
                                        <button
                                            type="button"
                                            className="pack-reset-prix-btn"
                                            onClick={() => {
                                                prixFinalLocked.current = false;
                                                setPackForm(prev => ({ ...prev, prixFinal: liveFinancials.totalTTC }));
                                            }}
                                        >
                                            🔄 Resynchroniser avec le prix TTC calculé
                                        </button>
                                    )}
                                </div>

                                {/* ─ Actions ─ */}
                                <div className="pm-form-actions">
                                    <button type="submit" className="pm-btn-submit">
                                        {editingPack ? '✅ Enregistrer les Modifications' : '🚀 Créer le Pack'}
                                    </button>
                                    <button
                                        type="button"
                                        className="pm-btn-cancel"
                                        onClick={() => {
                                            setShowForm(false);
                                            setEditingPack(null);
                                            setPackForm(emptyPackForm);
                                            prixFinalLocked.current = false;
                                        }}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* ── Barre de recherche / filtres ────────────────────── */}
                    <div className="pm-search-filter-bar packs-filter-glass">
                        <div className="pm-search-box">
                            <input
                                type="text"
                                className="pm-search-input"
                                placeholder="Rechercher par nom de pack..."
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                            />
                        </div>
                        <div className="pm-filter-controls">
                            <input
                                type="text"
                                className="pm-filter-select"
                                placeholder="Puissance (ex: 5 kWc)"
                                value={filterPuissance}
                                onChange={e => { setFilterPuissance(e.target.value); setPage(1); }}
                            />
                            <select
                                className="pm-filter-select"
                                value={filterStatut}
                                onChange={e => { setFilterStatut(e.target.value); setPage(1); }}
                            >
                                <option value="">Tous les statuts</option>
                                <option value="actif">Actif</option>
                                <option value="inactif">Inactif</option>
                            </select>
                            <button className="pm-btn-clear-filters" onClick={handleClearFilters}>
                                Réinitialiser
                            </button>
                        </div>
                    </div>

                    {/* ── Grille des packs ─────────────────────────────────── */}
                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <div className="packs-grid-container">
                            <div className="packs-elegant-grid">
                                {packs.map(pack => {
                                    const totalProds = (pack.categories || []).reduce(
                                        (s, c) => s + (c.produits?.length || 0), 0
                                    );
                                    const prixAffiche = pack.prixFinal ?? pack.prixCalcule ?? 0;
                                    const hasDiff = pack.prixFinal !== pack.prixCalcule;

                                    return (
                                        <div key={pack._id} className="pack-elegant-card" onClick={() => handleViewDetail(pack)} style={{ cursor: 'pointer' }}>
                                            <div className="pack-card-glow-overlay" />
                                            <div className="pack-card-header">
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <div className="pack-power-badge">{pack.puissance}</div>
                                                    {pack.reference && <span className="pack-ref-badge">Ref: {pack.reference}</span>}
                                                </div>
                                                <span className={`pack-status-pill ${pack.statut}`}>
                                                    {pack.statut === 'actif' ? 'Actif' : 'Inactif'}
                                                </span>
                                            </div>

                                            <div className="pack-card-body">
                                                <h3>{pack.nom}</h3>
                                                <p className="pack-description">
                                                    {pack.description || 'Aucune description fournie.'}
                                                </p>

                                                {/* Aperçu catégories */}
                                                <div className="pack-items-preview">
                                                    <h5>
                                                        {pack.categories?.length || 0} catégorie{pack.categories?.length !== 1 ? 's' : ''}
                                                        {' · '}{totalProds} produit{totalProds !== 1 ? 's' : ''}
                                                    </h5>
                                                    {(pack.categories || []).slice(0, 3).map((cat, ci) => (
                                                        <div key={ci} className="pack-preview-cat">
                                                            <span className="preview-cat-label">
                                                                📁 {cat.categorieId?.label || '—'}
                                                            </span>
                                                            <ul>
                                                                {(cat.produits || []).slice(0, 2).map((item, pi) => {
                                                                    const isLineaire = item.produitId?.modeGestionStock === 'lineaire';
                                                                    const decoupe = item.decoupeLineaire;
                                                                    let qtyDisplay;
                                                                    if (isLineaire && decoupe && decoupe.longueurPieceMetre) {
                                                                        const np = decoupe.nombrePieces || 1;
                                                                        const lp = Number(decoupe.longueurPieceMetre).toFixed(2);
                                                                        const total = Number(item.quantiteRecommandee).toFixed(2);
                                                                        qtyDisplay = np > 1
                                                                            ? `${np}×${lp}m = ${total}m`
                                                                            : `${lp} m`;
                                                                    } else if (isLineaire) {
                                                                        qtyDisplay = `${Number(item.quantiteRecommandee).toFixed(2)} m`;
                                                                    } else {
                                                                        qtyDisplay = `×${item.quantiteRecommandee}`;
                                                                    }
                                                                    return (
                                                                        <li key={pi}>
                                                                            <span>• {item.produitId?.nomProduit || 'Produit'}{isLineaire ? ' 📏' : ''}</span>
                                                                            <strong>{qtyDisplay}</strong>
                                                                        </li>
                                                                    );
                                                                })}
                                                                {cat.produits?.length > 2 && (
                                                                    <li className="more-products-indicator">
                                                                        +{cat.produits.length - 2} autre{cat.produits.length - 2 > 1 ? 's' : ''}…
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                    {pack.categories?.length > 3 && (
                                                        <p className="more-products-indicator">
                                                            Et {pack.categories.length - 3} autre{pack.categories.length - 3 > 1 ? 's' : ''} catégorie{pack.categories.length - 3 > 1 ? 's' : ''}…
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="pack-card-footer" onClick={(e) => e.stopPropagation()}>
                                                <div className="pack-price-box">
                                                    <div>
                                                        <span className="price-label">Prix Final</span>
                                                        <span className="price-value">{fmt(prixAffiche)} DT</span>
                                                    </div>
                                                    {hasDiff && (
                                                        <span className="price-calc-note">
                                                            Calculé : {fmt(pack.prixCalcule)} DT
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="pack-actions-buttons">
                                                    <button
                                                        className="p-btn-view"
                                                        title="Voir les détails"
                                                        onClick={(e) => { e.stopPropagation(); handleViewDetail(pack); }}
                                                    >
                                                        👁️ Détails
                                                    </button>
                                                    <button
                                                        className="p-btn-duplicate"
                                                        title="Dupliquer ce pack"
                                                        onClick={(e) => { e.stopPropagation(); handleOpenDuplicate(pack); }}
                                                    >
                                                        📋 Dupliquer
                                                    </button>
                                                    <button
                                                        className="p-btn-edit"
                                                        title="Modifier le pack"
                                                        onClick={(e) => { e.stopPropagation(); handleOpenEdit(pack); }}
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="p-btn-delete"
                                                        title="Supprimer le pack"
                                                        onClick={(e) => { e.stopPropagation(); handleDeletePack(pack._id); }}
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {packs.length === 0 && (
                                <div className="no-packs-found-card">
                                    <h3>Aucun Pack Trouvé</h3>
                                    <p>Créez des packs pour simplifier et standardiser vos affectations de produits solaires.</p>
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="packs-pagination">
                                    <button
                                        className="pagination-btn"
                                        disabled={page === 1}
                                        onClick={() => setPage(p => p - 1)}
                                    >
                                        Précédent
                                    </button>
                                    <span className="pagination-text">Page {page} sur {totalPages}</span>
                                    <button
                                        className="pagination-btn"
                                        disabled={page === totalPages}
                                        onClick={() => setPage(p => p + 1)}
                                    >
                                        Suivant
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PacksManagement;
